import { useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { trans } from '@lingoda/i18n';
import { Button, IconButton } from '../../buttons';
import TextField from '../../fields/TextField';
import { CheckIcon, TrashIcon } from '../../icons/lingodaIcons';
import ClickToReveal from '../../layout/ClickToReveal';
import DangerousHtml from '../../layout/DangerousHtml';
import { Grid } from '../../layout/Grid';
import { Typography } from '../../layout/Typography';
import InputAdornment from '../InputAdornment';

export interface CouponProps {
    isValidating: boolean;
    coupon?: string;
    couponDescription?: string;
    name: string;
    id?: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    onRemove: () => void;
    reveal?: boolean;
    keepInputAfterApply?: boolean;
}

const CouponField: React.FC<CouponProps> = ({
    isValidating,
    coupon,
    couponDescription,
    onChange,
    onBlur,
    onRemove,
    reveal,
    keepInputAfterApply,
    id = uuidV4(),
    ...props
}) => {
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
        },
        [onChange],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            onBlur(e.target.value);
        },
        [onBlur],
    );

    if (coupon && !keepInputAfterApply) {
        return (
            <Grid container spacing={1} alignItems="baseline">
                <Grid item>{trans('coupon-applied', {}, 'public-courses-summary')}</Grid>
                <Grid item>
                    <Typography variant="overline"> {coupon}</Typography>
                </Grid>
                {couponDescription && (
                    <Grid item>
                        <DangerousHtml>{couponDescription}</DangerousHtml>
                    </Grid>
                )}
                <Grid item>
                    <IconButton
                        aria-label="Remove coupon"
                        onClick={onRemove}
                        disabled={isValidating}
                    >
                        <TrashIcon size={20} />
                    </IconButton>
                </Grid>
            </Grid>
        );
    }

    const inputPropsWhenApplied = {
        endAdornment: (
            <InputAdornment position="end" sx={(theme) => ({ color: theme.colors.text.success })}>
                <CheckIcon size={20} />
            </InputAdornment>
        ),
        readOnly: true,
    };

    return (
        <ClickToReveal label={trans('have-a-coupon', {}, 'public-courses-summary')} reveal={reveal}>
            <Grid container spacing={2} alignItems="center">
                <Grid item flex={1}>
                    <TextField
                        id={id}
                        type="text"
                        variant="outlined"
                        placeholder={trans('coupon-placeholder', {}, 'public-courses-summary')}
                        label={trans('coupon-label', {}, 'public-courses-summary')}
                        InputProps={coupon ? inputPropsWhenApplied : undefined}
                        autoComplete="off"
                        fullWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        {...props}
                        value={coupon ? coupon : props.value}
                    />
                </Grid>
                <Grid item>
                    {coupon ? (
                        <IconButton
                            aria-label="Remove coupon"
                            onClick={onRemove}
                            disabled={isValidating}
                        >
                            <TrashIcon size={24} />
                        </IconButton>
                    ) : (
                        <Button
                            color="primary"
                            variant={keepInputAfterApply ? 'contained' : 'outlined'}
                            aria-label={trans('apply-coupon', {}, 'public-courses-summary')}
                            type="submit"
                            loading={isValidating}
                            disabled={!props.value}
                        >
                            {trans('apply-coupon', {}, 'public-courses-summary')}
                        </Button>
                    )}
                </Grid>
            </Grid>
        </ClickToReveal>
    );
};

export default CouponField;
